import copy from "copy-to-clipboard";
import React, { PropsWithChildren, cloneElement, isValidElement } from "react";

type CopyToClipboardProps = {
	text: string;
	onCopy?: (text: string, result: boolean) => void;

	/**
	 * This is a typescript utility function that will take the parameters of the
	 * copy function.
	 */
	copyOptions?: Parameters<typeof copy>[1];
};

/**
 * A react component that will copy the text to the clipboard when clicked.
 *
 * Inspired by: https://github.com/nkbt/react-copy-to-clipboard
 */
export const CopyToClipboard = (props: PropsWithChildren<CopyToClipboardProps>) => {
	const handleClick = () => {
		const elem = React.Children.only(props.children);
		const result = copy(props.text, props.copyOptions);

		if (result && props.onCopy) {
			props.onCopy(props.text, result);
		}

		/**
		 * Bypass the onClick event if the user has provided a custom onClick
		 * event.
		 */
		if (isValidElement(elem) && typeof elem.props.onClick === "function") {
			elem.props.onClick();
		}
	};

	/**
	 * This is a helper function that will throw an error if there is more
	 * than one child
	 */
	const elem = React.Children.only(props.children);

	if (isValidElement(elem)) {
		return cloneElement(elem, { ...props, onClick: handleClick } as React.Attributes);
	}

	return null;
};
